import AsyncStorage from '@react-native-async-storage/async-storage';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const VAR_VOYAGER_CODE = 'voyagerCode';
const VAR_COMPLETED_PERMISSIONS = "completedPermissions";
const VAR_COMPLETED_OVERTURE = "completedOverture";
const VAR_COMPLETED_FINALE = "completedFinale";
const VAR_VISITED = "completedLocations";
const VAR_SELECTED_LOCATION = "selectedLocation";

export const StorageContext = createContext();

export const StorageProvider = ({ children }) => {
  // Cached storage state
  const [voyagerCode, setVoyagerCodeState] = useState(null);
  const [hasCompletedPermissions, setHasCompletedPermissionsState] = useState(false);
  const [hasCompletedOverture, setHasCompletedOvertureState] = useState(false);
  const [hasCompletedFinale, setHasCompletedFinaleState] = useState(false);
  const [visitedLocations, setVisitedLocationsState] = useState([]);
  const [selectedLocation, setSelectedLocationState] = useState(null);

  // Loading states - managed externally by JourneyContext
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load all storage data in parallel
      const [
        storedVoyagerCode,
        storedCompletedPermissions,
        storedCompletedOverture,
        storedCompletedFinale,
        storedVisitedLocs,
        storedSelectedLocation
      ] = await Promise.all([
        getVoyagerCode(),
        getCompletedPermissions(),
        getCompletedOverture(),
        getCompletedFinale(),
        getVisitedLocations(),
        getSelectedLocation()
      ]);

      // Update state with loaded data
      setVoyagerCodeState(storedVoyagerCode);
      setHasCompletedPermissionsState(storedCompletedPermissions);
      setHasCompletedOvertureState(storedCompletedOverture);
      setHasCompletedFinaleState(storedCompletedFinale);
      setVisitedLocationsState(storedVisitedLocs);
      setSelectedLocationState(storedSelectedLocation);
    } catch (err) {
      console.error('Error loading initial storage data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize storage data on mount
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);
  
  // Storage operations (internal - direct AsyncStorage access)
  const getCompletedPermissions = async () => {
    try {
      const value = await AsyncStorage.getItem(VAR_COMPLETED_PERMISSIONS);
      return value === 'true';
    } catch (err) {
      console.error('Error getting completed permissions:', err);
      throw err;
    }
  };

  const getCompletedOverture = async () => {
    try {
      const value = await AsyncStorage.getItem(VAR_COMPLETED_OVERTURE);
      return value === 'true';
    } catch (err) {
      console.error('Error getting completed overture:', err);
      throw err;
    }
  };

  const getCompletedFinale = async () => {
    try {
      const value = await AsyncStorage.getItem(VAR_COMPLETED_FINALE);
      return value === 'true';
    } catch (err) {
      console.error('Error getting completed finale:', err);
      throw err;
    }
  };

  const getVisitedLocations = async () => {
    try {
      const finishedLocations = await AsyncStorage.getItem(VAR_VISITED);
      if (!(finishedLocations) || finishedLocations === "null") {
        return [];
      } else {
        return finishedLocations.split(',');
      }
    } catch (err) {
      console.error('Error getting visited locations:', err);
      throw err;
    }
  }; 

  const getVoyagerCode = async () => {
    try {
      return await AsyncStorage.getItem(VAR_VOYAGER_CODE);
    } catch (err) {
      console.error('Error getting voyager code:', err);
      throw err;
    }
  };

  const getSelectedLocation = async () => {
    try {
      const locationJson = await AsyncStorage.getItem(VAR_SELECTED_LOCATION);
      return locationJson ? JSON.parse(locationJson) : null;
    } catch (err) {
      console.error('Error getting selected location:', err);
      throw err;
    }
  };

  // Public API methods (updates both state and storage)
  const setCompletedPermissions = async (completed) => {
    try {
      await AsyncStorage.setItem(VAR_COMPLETED_PERMISSIONS, completed ? 'true' : 'false');
      setHasCompletedPermissionsState(completed);
    } catch (err) {
      console.error('Error setting completed permissions:', err);
      setError(err.message);
      throw err;
    }
  };

  const setCompletedOverture = async (completed) => {
    try {
      await AsyncStorage.setItem(VAR_COMPLETED_OVERTURE, completed ? 'true' : 'false');
      setHasCompletedOvertureState(completed);
    } catch (err) {
      console.error('Error setting completed overture:', err);
      setError(err.message);
      throw err;
    }
  };

  const setCompletedFinale = async (completed) => {
    try {
      await AsyncStorage.setItem(VAR_COMPLETED_FINALE, completed ? 'true' : 'false');
      setHasCompletedFinaleState(completed);
    } catch (err) {
      console.error('Error setting completed finale:', err);
      setError(err.message);
      throw err;
    }
  };

  const setVoyagerCode = async (code) => {
    try {
      await AsyncStorage.setItem(VAR_VOYAGER_CODE, code);
      setVoyagerCodeState(code);
    } catch (err) {
      console.error('Error setting voyager code:', err);
      setError(err.message);
      throw err;
    }
  };

  const setSelectedLocation = async (location) => {
    try {
      if (location) {
        await AsyncStorage.setItem(VAR_SELECTED_LOCATION, JSON.stringify(location));
      } else {
        await AsyncStorage.removeItem(VAR_SELECTED_LOCATION);
      }
      setSelectedLocationState(location);
    } catch (err) {
      console.error('Error setting selected location:', err);
      setError(err.message);
      throw err;
    }
  };

  const setVisitedLocations = async (locations) => {
    try {
      const locationIds = Array.isArray(locations) ? locations : [locations];
      await AsyncStorage.setItem(VAR_VISITED, locationIds.join(','));
      setVisitedLocationsState(locationIds);
    } catch (err) {
      console.error('Error setting visited locations:', err);
      setError(err.message);
      throw err;
    }
  };

  const addVisitedLocation = async (locationId) => {
    try {
      const newVisitedLocations = [...visitedLocations, locationId];
      await setVisitedLocations(newVisitedLocations);
    } catch (err) {
      console.error('Error adding visited location:', err);
      setError(err.message);
      throw err;
    }
  };

  const reset = async () => {
    try {
      // Also remove the persisted voyager/login code so a full reset clears login persistence
      await AsyncStorage.multiRemove([
        VAR_VOYAGER_CODE,
        VAR_COMPLETED_PERMISSIONS,
        VAR_COMPLETED_OVERTURE,
        VAR_COMPLETED_FINALE,
        VAR_VISITED,
        VAR_SELECTED_LOCATION
      ]);

      // Reset state
      setHasCompletedPermissionsState(false);
      setHasCompletedOvertureState(false);
      setHasCompletedFinaleState(false);
      setVisitedLocationsState([]);
      setVoyagerCodeState(null);
      setSelectedLocationState(null);

      setError(null);
    } catch (err) {
      console.error('Error resetting storage:', err);
      setError(err.message);
      throw err;
    }
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <StorageContext.Provider value={{
      // Cached state
      voyagerCode,
      hasCompletedPermissions,
      hasCompletedOverture,
      hasCompletedFinale,
      visitedLocations,
      selectedLocation,
      
      // Loading states
      isLoading,
      error,
      
      // Operations
      setCompletedPermissions,
      setCompletedOverture,
      setCompletedFinale,
      setVoyagerCode,
      setVisitedLocations,
      setSelectedLocation,
      addVisitedLocation,
      reset,
      clearError,
      
      // Internal state setters for JourneyContext to manage loading
      setIsLoading,
      setError,
      
      // Utility
      refresh: loadInitialData
    }}>
      {children}
    </StorageContext.Provider>
  );
};

// Custom hook for using Storage context
export const useStorage = () => {
  const context = useContext(StorageContext);
  if (!context) {
    throw new Error('useStorage must be used within a StorageProvider');
  }
  return context;
};

export default StorageProvider;